/**
 * Shared saved views, managed centrally — Step 42.
 *
 * Kai: *"Views should also be able to made global, or shared with a group, or
 * groups, or just personal. Admins should be able to change and add views for
 * different groups on the backend."* The first three landed with the rail;
 * this is the last clause, and it was the piece still missing when the step
 * was reviewed.
 *
 * Ownership alone could not express it. A service desk's shared views outlive
 * whoever happened to create them, so when that person leaves, an org-wide
 * view nobody can edit becomes permanent furniture. The API now lets an admin
 * take over any SHARED view; private views stay with their owner, because
 * being an admin is not a reason to reach into somebody's own working set.
 *
 * Read-mostly by design: this page renames, re-scopes, re-groups, stars and
 * deletes. It does not edit FILTERS — those are built in the queue, where you
 * can see what they match, and a condition builder with no result count
 * beside it is how you ship a view that returns nothing.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, LayoutList, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { ticketsApi } from '../../features/tickets/api';
import { ViewEditModal } from '../tickets/ViewEditModal';
import type { SavedView } from '../../features/tickets/views';
import { cn } from '../../lib/utils';
import { useConfirm } from '../../lib/confirm';

interface ManagedView extends SavedView {
  ownerName: string | null;
  ownerEmail: string | null;
  updatedAt: number;
}

const SCOPE_LABEL: Record<string, string> = {
  org: 'Everyone',
  group: 'Chosen groups',
};

export function SavedViewsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<SavedView | null>(null);

  const views = useQuery({
    queryKey: qk.viewsManage,
    queryFn: () => apiFetch<{ entries: ManagedView[] }>('/api/views/manage'),
  });
  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });
  const groupName = (id: string) => groups.data?.entries.find((g) => g.id === id)?.name ?? 'Unknown group';

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.viewsManage });
    // The rail reads its own list, and a re-scoped view may have just left or
    // joined it.
    void qc.invalidateQueries({ queryKey: qk.views });
  };

  const star = useMutation({
    mutationFn: (v: ManagedView) => apiSend(`/api/views/${v.id}`, 'PATCH', { isStarred: !v.isStarred }),
    onSuccess: (r) => (r.ok ? invalidate() : toast.error(r.error ?? 'Could not save that.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiSend(`/api/views/${id}`, 'DELETE'),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not delete that view.');
      toast.success('View deleted.');
      invalidate();
    },
  });

  const entries = views.data?.entries ?? [];

  return (
    <div className="max-w-4xl space-y-4 p-6">
      <div>
        <h1 className="text-lg font-semibold">Shared views</h1>
        <p className="text-sm text-muted-foreground">
          Views shared with the desk or a group. Personal views aren't listed.
        </p>
      </div>

      {views.isError && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">Could not load the views</p>
            <Button size="sm" variant="outline" onClick={() => void views.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {views.isPending && (
        <Card>
          <CardContent className="space-y-3 p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {views.data && entries.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Nothing is shared yet. Build a filter in the queue, save it, and choose who can see it.
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {entries.map((v) => (
              <div
                key={v.id}
                className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0 hover:bg-accent/40"
              >
                <button
                  type="button"
                  onClick={() => star.mutate(v)}
                  aria-label={v.isStarred ? `Unstar ${v.name}` : `Star ${v.name}`}
                  className="rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Star
                    className={cn(
                      'h-4 w-4',
                      v.isStarred ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground',
                    )}
                  />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{v.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {v.ownerName ?? v.ownerEmail ?? 'Unknown owner'}
                    {v.pageSize ? ` · ${v.pageSize} rows` : ''}
                  </p>
                </div>
                <div className="hidden min-w-0 flex-1 flex-wrap gap-1 md:flex">
                  <Badge variant="outline">{SCOPE_LABEL[v.scope] ?? v.scope}</Badge>
                  {v.scope === 'group' &&
                    (v.groupIds?.length ? (
                      v.groupIds.map((g) => (
                        <Badge key={g} variant="outline" className="font-normal">
                          {groupName(g)}
                        </Badge>
                      ))
                    ) : (
                      // A group view with no groups reaches nobody but its
                      // owner. Said plainly, because it looks fine otherwise.
                      <Badge variant="outline" className="border-destructive/40 text-destructive">
                        No groups — nobody sees this
                      </Badge>
                    ))}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/tickets?view=${v.id}`)}
                  title="Open in the queue"
                  aria-label={`Open ${v.name}`}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(v)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: `Delete “${v.name}”?`,
                        body: 'Everyone who uses it loses it.',
                        destructive: true,
                        confirmLabel: 'Delete',
                      })
                    )
                      remove.mutate(v.id);
                  }}
                  aria-label={`Delete ${v.name}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <LayoutList className="h-3.5 w-3.5" />
        Filters and columns are edited in the queue itself — open a view, change it, and Save.
      </p>

      {/* The SAME modal the rail uses. A second editor would be a second set
          of rules about what a scope means. */}
      <ViewEditModal
        view={editing}
        onClose={() => {
          setEditing(null);
          invalidate();
        }}
      />
    </div>
  );
}
